import { createMuiTheme } from "@material-ui/core/styles";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";

/**
 * Standardized width intended for content within the website.
 * Narrower width improves readability.
 * The ideal line length is 40-60 characters per line for English body text.
 * @see: https://material.io/design/typography/understanding-typography.html#readability
 **/
export const contentMaxWidth = 70;
export const contentMaxWidthText = 44;
export const contextMaxWidthNarrow = 32;

/**
 * Layout constants
 */
export const layoutVerticalPadding = 5;

/** --- thomasuta.de color palette --- */
export const caraThemeBlue01 = "#E9EAEA";
export const caraThemeBlue02 = "#D4D5D6";
export const caraThemeBlue03 = "#BEC0C2";
export const caraThemeBlue04 = "#A9ABAE";
export const caraThemeBlue05 = "#94969A";
export const caraThemeBlue06 = "#7E8185";
export const caraThemeBlue07 = "#696C71";
export const caraThemeBlue08 = "#54575D";
export const caraThemeBlue09 = "#3E4249";
export const caraThemeBlue10 = "#292D35";
export const caraThemeBlue = "#141821";
export const caraThemeBlue12 = "#13161E";
export const caraThemeBlue13 = "#11141C";
export const caraThemeBlue14 = "#0F1219";
export const caraThemeBlue15 = "#0D1015";
export const caraThemeBlue16 = "#0B0E12";
export const caraThemeBlue17 = "#0A0B0F";
export const caraThemeBlue18 = "#08090C";
export const caraThemeBlue19 = "#060709";
export const caraThemeBlue20 = "#040506";
export const caraThemeBlue21 = "#020303";

/** --- Oberion branded color palette --- */
export const footerBackground = "#001935";
export const oberionBlue1 = "#312782";
export const oberionBlue2 = "#4830B2";
export const oberionBlue3 = "#6854E5";
export const oberionBlue4 = "#877AFF";
export const oberionBlue5 = "#A2A0FF";
export const grey1 = "#1e1e1e";
export const grey2 = "#3c3c3c";
export const grey3 = "#5a5a5a";
export const grey4 = "#787878";
export const grey5 = "#969696";
export const grey6 = "#b4b4b4";
export const grey7 = "#d2d2d2";
export const grey8 = "#f0f0f0";
export const purple1 = "#430996";
export const purple2 = "#4600C8";
export const purple3 = "#823FFC";
export const purple4 = "#996BFF";
export const purple5 = "#DCCCFF";
export const blue1 = "#008BB3";
export const blue2 = "#00B2CC";
export const blue3 = "#0CDFF2";
export const blue4 = "#6EFFFF";
export const blue5 = "#BFFFFF";
export const teal1 = "#008E76";
export const teal2 = "#00BFA5";
export const teal3 = "#00E5C6";
export const teal4 = "#5DF2D6";
export const teal5 = "#99FFEC";
export const green1 = "#00993F";
export const green2 = "#00C853";
export const green3 = "#50DC4B";
export const green4 = "#C3F528";
export const green5 = "#DEFF7C";
export const yellow1 = "#F99A00";
export const yellow2 = "#FFB43D";
export const yellow3 = "#FFDD00";
export const yellow4 = "#FFE859";
export const yellow5 = "#FFF199";
export const red1 = "#AF0000";
export const red2 = "#DC1E00";
export const red3 = "#F15A24";
export const red4 = "#FF8259";
export const red5 = "#FFAB9E";

/** --- Coolors palette --- */
export const linkViolet = "#4600C8";
export const stackoverflowDarkThemeGreen = "#13552F";


/** Properties to customize Material UI Typography. */
export const pxToRem = value => `${value / 16}rem`;
export const remToPx = value => `${value * 16}px`;
export const fontWeightLight = 300;
export const fontWeightRegular = 400;
export const fontWeightMedium = 500;
export const fontWeightBold = 600; // custom attribute


/** --- Material UI theme objects ---
 * Overriding styles: @see https://github.com/mui-org/material-ui/issues/11244#issuecomment-386792253
 */
const createdBreakpoints = createBreakpoints({});
const themeObject = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    type: "dark",
    background: {
      paper: "#190555",
      default: "#141821",
      // default: "#00002c",
    },
    action: {
      disabled: "rgba(0, 0, 0, 0.26)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
    },
    primary: {
      light: "#6350b2",
      main: "#312782",
      dark: "#000054",
      contrastText: "#fff",
    },
    secondary: {
      light: "#66fff7",
      main: "#03dac4",
      dark: "#00a794",
      contrastText: "#1f1f1f",
    },
    error: {
      light: "#e57373",
      main: "#f44336",
      dark: "#d32f2f",
      contrastText: "#fff",
    },
    text: {
      primary: "#ffffff",
      secondary: "#03dac4",
      disabled: "#b4b4b4",
      hint: "#00B2CC",
    },
  },
  overrides: {
    MuiTypography: {
      /**
       * Responsive Typography:
       * @see https://stackoverflow.com/questions/52472372/responsive-typography-in-material-ui
       *
       * Material Design specification:
       * @see Material Design: https://material.io/design/typography/the-type-system.html#type-scale
       *
       * @see MUI default values: node_modules\@material-ui\core\styles\createTypography.js
       var nextVariants = {
          //  buildVariant(fontWeight, size, lineHeight, letterSpacing)
          h1: buildVariant(fontWeightLight, 96, 1, -1.5),
          h2: buildVariant(fontWeightLight, 60, 1, -0.5),
          h3: buildVariant(fontWeightRegular, 48, 1.04, 0),
          h4: buildVariant(fontWeightRegular, 34, 1.17, 0.25),
          h5: buildVariant(fontWeightRegular, 24, 1.33, 0),
          h6: buildVariant(fontWeightMedium, 20, 1.6, 0.15),
          subtitle1: buildVariant(fontWeightRegular, 16, 1.75, 0.15),
          subtitle2: buildVariant(fontWeightMedium, 14, 1.57, 0.1),
          body1Next: buildVariant(fontWeightRegular, 16, 1.5, 0.15),
          body2Next: buildVariant(fontWeightRegular, 14, 1.5, 0.15),
          buttonNext: buildVariant(fontWeightMedium, 14, 1.75, 0.4, caseAllCaps),
          captionNext: buildVariant(fontWeightRegular, 12, 1.66, 0.4),
          overline: buildVariant(fontWeightRegular, 12, 2.66, 1, caseAllCaps)
        };
       * letterSpacing: space between letters in a word
       */
      h1: {
        fontWeight: fontWeightLight,
        fontSize: pxToRem(72),  // 96 customized
        [createdBreakpoints.down("xs")]: { fontSize: pxToRem(48) }, // 96 * 0.5
        lineHeight: 1,
        letterSpacing: -1.5,
      },
      h2: {
        fontWeight: fontWeightLight,
        fontSize: pxToRem(48),  // 60 customized
        [createdBreakpoints.down("xs")]: { fontSize: pxToRem(36) }, // 48 * 0.75
        lineHeight: 1,
        letterSpacing: -0.5,
      },
      h3: {
        fontWeight: fontWeightRegular,
        fontSize: pxToRem(36),  // 48 customized
        [createdBreakpoints.down("xs")]: { fontSize: pxToRem(30) }, // 34 * 0.75
        lineHeight: 1.04,
        letterSpacing: 0,
      },
      h4: {
        fontWeight: fontWeightRegular,
        fontSize: pxToRem(28),  // 34 customized
        [createdBreakpoints.down("xs")]: { fontSize: pxToRem(27) },
        lineHeight: 1.17,
        letterSpacing: 0.25,
      },
      h5: {
        fontWeight: fontWeightMedium,  // fontWeightRegular customized
        fontSize: pxToRem(24),
        lineHeight: 1.33,
        letterSpacing: 0,
      },
      h6: {
        fontWeight: fontWeightMedium,
        fontSize: pxToRem(21),
        lineHeight: 1.6,
        letterSpacing: 0.15,
      },
      subtitle1: {
        fontWeight: fontWeightRegular,
        fontSize: pxToRem(18),
        lineHeight: 1.75,
        letterSpacing: 0.15,
      },
      subtitle2: {
        fontWeight: fontWeightMedium,  // fontWeightRegular customized
        fontSize: pxToRem(18),   // 16 customized
        lineHeight: 1.57,
        letterSpacing: 0.1,
      },
      body1: {
        fontWeight: fontWeightRegular,
        fontSize: pxToRem(16),
        lineHeight: 1.7,               // 1.5 customized
        letterSpacing: 0.15,
      },
      body2: {
        fontWeight: fontWeightMedium,  // fontWeightRegular customized
        fontSize: pxToRem(16),   // 14 customized
        lineHeight: 1.6,
        letterSpacing: 0.15,
      },
      button: {
        fontWeight: fontWeightMedium,
        fontSize: pxToRem(16),  // 14 customized
        lineHeight: 1.75,
        letterSpacing: 0.4,
      },
      caption: {
        fontWeight: fontWeightRegular,
        fontSize: pxToRem(14),  // 12 customized
        lineHeight: 1.66,
        letterSpacing: 0.4,
      },
      overline: {
        fontWeight: fontWeightRegular,
        fontSize: pxToRem(14),  // 12 customized
        lineHeight: 2.66,
        letterSpacing: 1,
      },
    },
  },
};
const theme = createMuiTheme(themeObject);
export default theme;
